import { Fragment } from 'react'

import {
  Icon,
  IconCheck,
  IconLine,
  IconMail,
  IconMenu2,
  IconPercentage,
  IconPhone,
  IconX,
} from '@tabler/icons-react'
import { Input, InputNumber, Switch } from 'antd'
import { Rule } from 'antd/lib/form'
import { ColumnType } from 'antd/lib/table'

import { noDesiredState } from '../../templateRenderer/DisplayBlocks/ChecklistDisplayBlock'
import {
  Block,
  BlockKind,
  BlockTypes,
  CheckboxRadioField,
  DisplayBlock,
} from '../../types/blueprint'

import { RichTextInput } from '@/components/form-components/RichTextInput'
import { PillProps } from '@/components/shared-components/BUI/Pill'
import { TimezonedDateProps } from '@/context/timezoneContext'
import { Role } from '@/modules/adminUsers/roles'
import { CheckboxRadioInput } from '@/modules/templates/templateEditor/SideEditors/CustomInputs/CheckboxRadioInput/CheckboxRadioInput'
import { ListInput } from '@/modules/templates/templateEditor/SideEditors/CustomInputs/ListInput/ListInput'
import { SelectInput } from '@/modules/templates/templateEditor/SideEditors/CustomInputs/SelectInput/SelectInput'
import { UploaderInput } from '@/modules/templates/templateEditor/SideEditors/CustomInputs/UploaderInput/UploaderInput'

import { BuildPassModule, TemplateType } from 'types/api.graphql'

type FieldType = {
  component: React.ComponentType | typeof Switch | 'select'
  valuePropName?: string
}

export const FORBIDDEN_IN_APP = [
  TemplateType.CompanyInduction,
  TemplateType.SiteInduction,
  TemplateType.Toolbox,
  TemplateType.SignOn,
]

const RadioInput = (props) => <CheckboxRadioInput type="radio" {...props} />
const CheckInput = (props) => <CheckboxRadioInput type="checkbox" {...props} />

export const InputField: FieldType = { component: Input }
export const InputNumberField: FieldType = { component: InputNumber }
export const TextAreaField: FieldType = { component: Input.TextArea }
export const SelectField: FieldType = { component: 'select' }
export const CheckboxField: FieldType = { component: CheckInput }
export const RadioField: FieldType = { component: RadioInput }
export const RichTextField: FieldType = { component: RichTextInput }
export const ListField: FieldType = { component: ListInput }
export const SelectItemsField: FieldType = { component: SelectInput }
export const SwitchField: FieldType = {
  component: Switch,
  valuePropName: 'checked',
}
export const UploaderField: FieldType = { component: UploaderInput }

export type Option = {
  value: string
  label: string
  icon?: React.ComponentType
  default?: boolean
}

export type TypeOption = {
  value: BlockTypes
  label: string
  icon: Icon
  helpText?: string
  kind: BlockKind
  hideName?: boolean
  hideDescription?: boolean
  exclusiveTemplateTypes?: TemplateType[] // blocks are ONLY allowed in these template types
  forbiddenTemplateTypes?: TemplateType[] // blocks are NOT allowed in these template types
  builderModule?: BuildPassModule
  roles?: Role[]
}

export type Field = {
  name: string
  label: string
  helpText?: string
  field: FieldType
  options?: Option[] | TypeOption[]
  required?: boolean
  default?: boolean | string
  builderModule?: BuildPassModule
  roles?: Role[]
}

export const textTypeField: Field = {
  name: 'textType',
  label: 'Input type',
  field: SelectField,
  options: [
    {
      value: 'text',
      label: 'Text',
      icon: () => <IconLine size={16} />,
      default: true,
    },
    {
      value: 'textArea',
      label: 'Multi-line',
      icon: () => <IconMenu2 size={16} />,
    },
    { value: 'email', label: 'Email', icon: () => <IconMail size={16} /> },
    {
      value: 'number',
      label: 'Number',
      icon: () => <IconPercentage size={16} />,
    },
    { value: 'phone', label: 'Phone', icon: () => <IconPhone size={16} /> },
  ],
  required: true,
}

export const getBlockDesiredState = (
  field: CheckboxRadioField
): Partial<PillProps> => {
  if (!field || noDesiredState(field)) return {}
  if (field.desiredState === field.checked) {
    return { variant: 'success', icon: <IconCheck size={16} /> }
  }

  return { variant: 'danger', icon: <IconX size={16} /> }
}

export type ColumnOptions = {
  assertOverdue?: boolean
}

export type CsvColumn = {
  title: string
  originalBlockName: string
  blockId: string
  options: CsvColumnOptions | undefined
  type: BlockTypes
}

export type CsvColumnOptions = {
  dateRangeIndex?: 0 | 1
}

/* eslint-disable @typescript-eslint/no-unused-vars */
export abstract class BaseBlockConfig<T extends Block> {
  public abstract kind: T['kind']
  public abstract type: T['type']

  public abstract blockTypeConfig: TypeOption
  public abstract blockFieldsConfig: Field[]

  public enableBlockToCsvColumn = false
  public enableBlockToColumnDisplay = false // governs the "Show In Table" option in the block editor
  public enableAdminOnlyVisiblity = false

  public abstract mapFormValueToBlock(
    block: T,
    value: unknown,
    returnDefaultValue?: boolean
  ): T

  public mapBlockToDisplayBlock(block: T): DisplayBlock {
    if (block.kind === 'display') return block

    return { ...block, kind: 'display' } as DisplayBlock
  }

  public mapBlockToCsvValue(
    block: T,
    options: CsvColumnOptions | undefined,
    formatDateString: (props: TimezonedDateProps) => string
  ): string | null {
    throw new Error(
      `mapBlockToCsvValue Not implemented for block: ${block.type}-${block.kind}`
    )
  }

  public mapBlockToCsvColumn(title: string, block: T): CsvColumn[] {
    return [
      {
        title: title,
        originalBlockName: block.name,
        blockId: block.id,
        options: undefined,
        type: block.type,
      },
    ]
  }

  public mapBlockToColumnDisplay(
    block: T,
    options: ColumnOptions
  ): React.ReactNode {
    return <Fragment />
  }

  public mapBlockToColumnSort(
    block: T,
    enableDefaultSorting = true
  ): ColumnType<unknown> {
    return {}
  }

  protected getAandBBlocks = (columnBlock: T, a, b): [a: T, b: T] => {
    return [a[columnBlock.id] as T, b[columnBlock.id] as T]
  }

  public abstract renderBlock(block: T, rules?: Rule[]): React.ReactNode

  public renderDisplayBlock(block: DisplayBlock): React.ReactNode {
    return null
  }
}
